/**
 * API client for ScheduleItem endpoints
 * Used by Intake Calendar for CRUD operations on schedule items
 */

import { get, post, put, destroy } from "../../../api/axios-client";
import {
  buildScheduleItemsRequestBody,
  toDateOnlyString,
} from "../utils/woDateFilter";
import { getAuditUserName } from "../../common/Utils/auditUser";
import type {
  ScheduleItem,
  ScheduleItemSegment,
  CreateScheduleItemRequest,
  UpdateScheduleItemRequest,
  GetScheduleItemsFilter,
  ConvertToWorkOrderRequest,
  CreateWorkOrderFromScheduleRequest,
  CreateWorkOrderFromScheduleResponse,
  UpdateWorkOrderFromScheduleRequest,
  UpdateWorkOrderResponse,
  UpdateWorkOrderUnitRequest,
  UpdateWorkOrderCustomerRequest,
} from "../types/scheduleItem.types";

const controllerUrl = "api/IntakeCalendar/";
const workOrderControllerUrl = "api/WorkOrders/";

/**
 * Parse a date string as LOCAL time (no timezone conversion).
 * 
 * IMPORTANT: Our database stores naive datetime (no timezone info).
 * To ensure consistency between frontend display and database storage:
 * - We always strip timezone suffixes (Z or +HH:MM) from date strings
 * - We parse dates as local time
 * - We send dates without timezone info using toLocalISOString()
 * 
 * This means: if the database has "2026-04-10 10:00:00",
 * the frontend will display "10:00 AM" regardless of user's timezone.
 */
const parseLocalDate = (value: string | Date | null | undefined): Date => {
  if (!value) return new Date(NaN);
  if (value instanceof Date) return value;
  
  // Strip any timezone information to ensure we parse as local time
  // This handles: "2026-04-10T10:00:00Z", "2026-04-10T10:00:00+05:00", "2026-04-10T10:00:00"
  const localTimeStr = value
    .replace(/Z$/i, '')                    // Remove Z suffix (UTC marker)
    .replace(/[+-]\d{2}:\d{2}$/, '')       // Remove timezone offset (+05:00, -04:00, etc.)
    .replace(/[+-]\d{4}$/, '');            // Remove timezone offset without colon (+0500, -0400, etc.)
  
  // Parse as local time (no Z suffix = local time in JavaScript)
  return new Date(localTimeStr);
};

/**
 * Normalize the `segments` array returned by the API into the frontend shape.
 *
 * - The backend serializer may emit either camelCase or PascalCase depending on
 *   configuration, so every field is read with a fallback.
 * - Blank/invalid `billAs` values default to `"actual"` to match the DB default.
 * - The resulting list is sorted by `lineNumber` (then `segmentId`) so segments
 *   are always displayed in the original work-order order (1st segment first,
 *   2nd second, etc.) regardless of how the API returns them.
 */
const mapSegments = (rawSegments: unknown): ScheduleItemSegment[] => {
  if (!Array.isArray(rawSegments)) return [];

  const mapped: ScheduleItemSegment[] = rawSegments.map((raw: any) => {
    const billAsRaw = (raw.billAs ?? raw.BillAs ?? "actual") as string;
    const billAs: ScheduleItemSegment["billAs"] =
      billAsRaw?.toString().toLowerCase() === "flatrate" ? "flatrate" : "actual";

    const estimatedRaw = raw.estimatedHours ?? raw.EstimatedHours ?? 0;
    const estimatedHours =
      typeof estimatedRaw === "number"
        ? estimatedRaw
        : Number(estimatedRaw) || 0;

    const lineNumberRaw = raw.lineNumber ?? raw.LineNumber;
    const lineNumber =
      typeof lineNumberRaw === "number"
        ? lineNumberRaw
        : Number(lineNumberRaw);

    return {
      segmentId: raw.segmentId ?? raw.SegmentId ?? 0,
      serviceCodeId: raw.serviceCodeId ?? raw.ServiceCodeId,
      segmentTitle: raw.segmentTitle ?? raw.SegmentTitle ?? "",
      billAs,
      estimatedHours,
      lineNumber: Number.isFinite(lineNumber) ? lineNumber : undefined,
    };
  });

  // Stable sort by lineNumber, then segmentId as a tie-breaker. Segments
  // without a lineNumber sink to the end so any legacy rows don't push valid
  // rows out of order.
  return mapped
    .map((seg, originalIndex) => ({ seg, originalIndex }))
    .sort((a, b) => {
      const aLine =
        a.seg.lineNumber ?? Number.MAX_SAFE_INTEGER;
      const bLine =
        b.seg.lineNumber ?? Number.MAX_SAFE_INTEGER;
      if (aLine !== bLine) return aLine - bLine;
      if (a.seg.segmentId !== b.seg.segmentId) {
        return a.seg.segmentId - b.seg.segmentId;
      }
      return a.originalIndex - b.originalIndex;
    })
    .map(({ seg }) => seg);
};

const parseOptionalDateOnly = (
  value: string | Date | null | undefined,
): string | undefined => toDateOnlyString(value);

const parseOptionalLocalDate = (
  value: string | Date | null | undefined,
): Date | undefined => {
  if (value == null || value === "") return undefined;
  const parsed = parseLocalDate(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const ScheduleItemAPI = {
  // Get schedule items by date range
  getByDateRange: async (filter: GetScheduleItemsFilter): Promise<ScheduleItem[]> => {
    try {
      const requestBody = buildScheduleItemsRequestBody(filter);
      console.log('[ScheduleItemAPI] GetByDateRange request body:', JSON.stringify(requestBody));
      const { data } = await post(`${controllerUrl}GetByDateRange`, requestBody);
      console.log('[ScheduleItemAPI] GetByDateRange raw response:', data);
      const response = data.content || data.Content || data.Data || data.data || data || [];
      
      // Ensure response is an array
      if (!Array.isArray(response)) {
        console.warn("[ScheduleItemAPI] Response is not an array:", response);
        return [];
      }
      
      // Convert date strings to Date objects
      return response.map((item: any) => ({
        ...item,
        scheduleItemId: item.scheduleItemId ?? item.ScheduleItemId,
        customerId: item.customerId ?? item.CustomerId,
        customerName: item.customerName ?? item.CustomerName ?? '',
        customerPhone: item.customerPhone ?? item.CustomerPhone,
        customerEmail: item.customerEmail ?? item.CustomerEmail,
        locationId: item.locationId ?? item.LocationId,
        unitId: item.unitId ?? item.UnitId,
        unitNumber: item.unitNumber ?? item.UnitNumber,
        unitDescription: item.unitDescription ?? item.UnitDescription,
        workOrderId: item.workOrderId ?? item.WorkOrderId,
        workOrderNumber: item.workOrderNumber ?? item.WorkOrderNumber,
        callInDateTime: parseOptionalLocalDate(
          item.callInDateTime ?? item.CallInDateTime,
        ),
        startDateTime: parseLocalDate(item.startDateTime ?? item.StartDateTime),
        endDateTime: parseLocalDate(item.endDateTime ?? item.EndDateTime),
        title: item.title ?? item.Title ?? '',
        description: item.description ?? item.Description,
        status: item.status ?? item.Status ?? 'Scheduled',
        timeZoneId: item.timeZoneId ?? item.TimeZoneId ?? 'Eastern Standard Time',
        // Work Order fields
        woTechnicianId: item.woTechnicianId ?? item.WoTechnicianId,
        woPriorityId: item.woPriorityId ?? item.WoPriorityId,
        woStatusId: item.woStatusId ?? item.WoStatusId,
        woSalesPersonId: item.woSalesPersonId ?? item.WoSalesPersonId,
        woStatus: item.woStatus ?? item.WoStatus,
        woPickupDate: parseOptionalDateOnly(
          item.woPickupDate ?? item.WoPickupDate,
        ),
        woDeliveryDate: parseOptionalDateOnly(
          item.woDeliveryDate ?? item.WoDeliveryDate,
        ),
        // Single-segment fields (first segment - backward compat)
        segmentId: item.segmentId ?? item.SegmentId,
        serviceCodeId: item.serviceCodeId ?? item.ServiceCodeId,
        segmentTitle: item.segmentTitle ?? item.SegmentTitle,
        // Full segment list (new) - used to bind multi-segment editor
        segments: mapSegments(item.segments ?? item.Segments),
        // Audit fields
        createdBy: item.createdBy ?? item.CreatedBy,
        createdByName: item.createdByName ?? item.CreatedByName ?? '',
        createdDate: parseLocalDate(item.createdDate ?? item.CreatedDate),
        modifiedBy: item.modifiedBy ?? item.ModifiedBy,
        modifiedByName: item.modifiedByName ?? item.ModifiedByName ?? '',
        modifiedDate: parseLocalDate(item.modifiedDate ?? item.ModifiedDate),
        rowVersion: item.rowVersion ?? item.RowVersion,
      })) as ScheduleItem[];
    } catch (error: any) {
      console.error("[ScheduleItemAPI] Error fetching schedule items:", error);
      throw error;
    }
  },

  // Get a single schedule item by ID
  getById: async (scheduleItemId: number): Promise<ScheduleItem | null> => {
    try {
      const { data } = await get(`${controllerUrl}GetById?scheduleItemId=${scheduleItemId}`);
      const item = data.content || data.Content || data.Data || data.data || data;
      
      if (!item) return null;
      
      return {
        ...item,
        scheduleItemId: item.scheduleItemId ?? item.ScheduleItemId,
        customerId: item.customerId ?? item.CustomerId,
        customerName: item.customerName ?? item.CustomerName ?? '',
        customerPhone: item.customerPhone ?? item.CustomerPhone,
        customerEmail: item.customerEmail ?? item.CustomerEmail,
        locationId: item.locationId ?? item.LocationId,
        unitId: item.unitId ?? item.UnitId,
        unitNumber: item.unitNumber ?? item.UnitNumber,
        unitDescription: item.unitDescription ?? item.UnitDescription,
        workOrderId: item.workOrderId ?? item.WorkOrderId,
        workOrderNumber: item.workOrderNumber ?? item.WorkOrderNumber,
        callInDateTime: parseOptionalLocalDate(
          item.callInDateTime ?? item.CallInDateTime,
        ),
        startDateTime: parseLocalDate(item.startDateTime ?? item.StartDateTime),
        endDateTime: parseLocalDate(item.endDateTime ?? item.EndDateTime),
        title: item.title ?? item.Title ?? '',
        description: item.description ?? item.Description,
        status: item.status ?? item.Status ?? 'Scheduled',
        timeZoneId: item.timeZoneId ?? item.TimeZoneId ?? 'Eastern Standard Time',
        // Work Order fields
        woTechnicianId: item.woTechnicianId ?? item.WoTechnicianId,
        woPriorityId: item.woPriorityId ?? item.WoPriorityId,
        woStatusId: item.woStatusId ?? item.WoStatusId,
        woSalesPersonId: item.woSalesPersonId ?? item.WoSalesPersonId,
        woStatus: item.woStatus ?? item.WoStatus,
        woPickupDate: parseOptionalDateOnly(
          item.woPickupDate ?? item.WoPickupDate,
        ),
        woDeliveryDate: parseOptionalDateOnly(
          item.woDeliveryDate ?? item.WoDeliveryDate,
        ),
        // Single-segment fields (first segment - backward compat)
        segmentId: item.segmentId ?? item.SegmentId,
        serviceCodeId: item.serviceCodeId ?? item.ServiceCodeId,
        segmentTitle: item.segmentTitle ?? item.SegmentTitle,
        // Full segment list (new)
        segments: mapSegments(item.segments ?? item.Segments),
        // Audit fields
        createdBy: item.createdBy ?? item.CreatedBy,
        createdByName: item.createdByName ?? item.CreatedByName ?? '',
        createdDate: parseLocalDate(item.createdDate ?? item.CreatedDate),
        modifiedBy: item.modifiedBy ?? item.ModifiedBy,
        modifiedByName: item.modifiedByName ?? item.ModifiedByName ?? '',
        modifiedDate: parseLocalDate(item.modifiedDate ?? item.ModifiedDate),
        rowVersion: item.rowVersion ?? item.RowVersion,
      } as ScheduleItem;
    } catch (error: any) {
      console.error("Error fetching schedule item:", error);
      throw error;
    }
  },

  // Create a new schedule item
  create: async (request: CreateScheduleItemRequest): Promise<number> => {
    try {
      const { data } = await post(`${controllerUrl}Create`, {
        ...request,
        callInDateTime: request.callInDateTime ?? null,
        startDateTime: request.startDateTime,
        endDateTime: request.endDateTime,
      });
      return data.content || data.Content || data.Data || data.data || data;
    } catch (error: any) {
      console.error("Error creating schedule item:", error);
      throw error;
    }
  },

  // Update an existing schedule item
  update: async (request: UpdateScheduleItemRequest): Promise<boolean> => {
    try {
      const { data } = await put(`${controllerUrl}Update`, {
        scheduleItemId: request.scheduleItemId,
        customerId: request.customerId,
        locationId: request.locationId,
        unitId: request.unitId,
        startDateTime: request.startDateTime,
        endDateTime: request.endDateTime,
        title: request.title,
        description: request.description,
        status: request.status,
        timeZoneId: request.timeZoneId,
        modifiedBy: request.modifiedBy,
        rowVersionBase64: request.rowVersion, // Already a Base64 string from API
      });
      
      // Check if the update was successful
      const isSuccess = data.isSuccess ?? data.IsSuccess ?? true;
      const content = data.content ?? data.Content ?? data.Data ?? data.data ?? data;
      
      if (!isSuccess) {
        const message = data.message ?? data.Message ?? 'Update failed. The record may have been modified by another user.';
        throw new Error(message);
      }
      
      return content;
    } catch (error: any) {
      console.error("Error updating schedule item:", error);
      throw error;
    }
  },

  // Delete (cancel) a schedule item
  delete: async (scheduleItemId: number): Promise<boolean> => {
    try {
      const { data } = await destroy(`${controllerUrl}Delete?scheduleItemId=${scheduleItemId}`);
      return data.content || data.Content || data.Data || data.data || data;
    } catch (error: any) {
      console.error("Error deleting schedule item:", error);
      throw error;
    }
  },

  // Convert schedule item to Work Order
  convertToWorkOrder: async (request: ConvertToWorkOrderRequest): Promise<number> => {
    try {
      const { data } = await post(`${controllerUrl}ConvertToWorkOrder`, {
        scheduleItemId: request.scheduleItemId,
        woStatusIdentity: request.woStatusIdentity,
        technicianIdentity: request.technicianIdentity,
        createdBy: request.createdBy,
        rowVersionBase64: request.rowVersion, // Send as Base64 string
      });
      return data.content || data.Content || data.Data || data.data || data;
    } catch (error: any) {
      console.error("Error converting schedule item to work order:", error);
      throw error;
    }
  },

  /**
   * Create a work order from a schedule item
   * Supports both single segment (backward compat) and multiple segments
   */
  createWorkOrderFromSchedule: async (
    request: CreateWorkOrderFromScheduleRequest
  ): Promise<CreateWorkOrderFromScheduleResponse> => {
    try {
      const auditUser = getAuditUserName();
      const createdBy = request.createdBy?.trim() || auditUser;
      const modifiedBy = request.modifiedBy?.trim() || auditUser;

      // Build request payload - supports both single and multiple segments
      const requestPayload: Record<string, unknown> = {
        customerIdentity: request.customerIdentity,
        locationIdentity: request.locationIdentity,
        unitIdentity: request.unitIdentity,
        woStatusIdentity: request.woStatusIdentity,
        woPriorityIdentity: request.woPriorityIdentity,
        salesPersonIdentity: request.salesPersonIdentity,
        technicianIdentity: request.technicianIdentity,
        status: request.status,
        actionType: request.actionType,
        createdBy,
        createdDate: request.createdDate,
        modifiedBy,
        modifiedDate: request.modifiedDate,
        customerScheduleItemId: request.scheduleItemId,
      };

      // Use segments array if provided, otherwise use single segment for backward compatibility
      if (request.segments && request.segments.length > 0) {
        requestPayload.segments = request.segments;
      } else if (request.segmentTitle && request.serviceCodeIdentity) {
        requestPayload.segment = {
          displayText: request.segmentTitle,
          serviceCodeIdentity: request.serviceCodeIdentity,
          billAs: "actual",
          estimatedHours: 0,
        };
      }

      // Add schedule info if technician is assigned
      if (request.schedule) {
        requestPayload.schedule = request.schedule;
      }

      const { data } = await post(
        `${workOrderControllerUrl}CreateSmartWOForTechCalendar`,
        requestPayload
      );

      const result = data.content || data.Content || data.Data || data.data || data;

      if (!result || result.isSuccess === false) {
        throw new Error(
          result?.message || result?.errorMessage || "Failed to create work order"
        );
      }

      if (!result.invoiceDocumentIdentity || result.invoiceDocumentIdentity === 0) {
        throw new Error(
          result?.message || "Failed to create work order - invalid response from server"
        );
      }

      return {
        invoiceDocumentIdentity: result.invoiceDocumentIdentity,
        documentNumber: result.documentNumber || "",
        segmentId: result.segmentId,
        scheduleItemId: result.scheduleItemId,
        segmentResults: result.segmentResults,
        message: result.message || "Work order created successfully",
      };
    } catch (error: unknown) {
      console.error("Error creating work order from schedule:", error);
      throw error;
    }
  },

  /**
   * Update an existing work order from a schedule item
   * Updates InvoiceHeader and InvoiceSegment fields
   */
  updateWorkOrderFromSchedule: async (
    request: UpdateWorkOrderFromScheduleRequest
  ): Promise<UpdateWorkOrderResponse> => {
    try {
      const { data } = await post(
        `${workOrderControllerUrl}UpdateWorkOrderFromSchedule`,
        request
      );

      const result = data.content || data.Content || data.Data || data.data || data;

      if (!result || result.success === false) {
        throw new Error(
          result?.message || "Failed to update work order"
        );
      }

      return {
        success: result.success ?? true,
        message: result.message || "Work order updated successfully",
      };
    } catch (error: any) {
      console.error("Error updating work order from schedule:", error);
      throw error;
    }
  },

  /**
   * Update unit information in work order
   * Fetches unit details from UnitBase and updates InvoiceHeader
   */
  updateWorkOrderUnit: async (
    request: UpdateWorkOrderUnitRequest
  ): Promise<UpdateWorkOrderResponse> => {
    try {
      const { data } = await post(
        `${workOrderControllerUrl}UpdateWorkOrderUnit`,
        request
      );

      const result = data.content || data.Content || data.Data || data.data || data;

      if (!result || result.success === false) {
        throw new Error(
          result?.message || "Failed to update unit in work order"
        );
      }

      return {
        success: result.success ?? true,
        message: result.message || "Unit updated successfully",
      };
    } catch (error: any) {
      console.error("Error updating unit in work order:", error);
      throw error;
    }
  },

  /**
   * Update customer information in work order
   * Fetches customer details from Customer table and updates InvoiceHeader
   */
  updateWorkOrderCustomer: async (
    request: UpdateWorkOrderCustomerRequest
  ): Promise<UpdateWorkOrderResponse> => {
    try {
      const { data } = await post(
        `${workOrderControllerUrl}UpdateWorkOrderCustomer`,
        request
      );

      const result = data.content || data.Content || data.Data || data.data || data;

      if (!result || result.success === false) {
        throw new Error(
          result?.message || "Failed to update customer in work order"
        );
      }

      return {
        success: result.success ?? true,
        message: result.message || "Customer updated successfully",
      };
    } catch (error: any) {
      console.error("Error updating customer in work order:", error);
      throw error;
    }
  },
};

export default ScheduleItemAPI;
